import { chain, sumBy } from 'lodash';
import SURVEY_CHART_TYPE from '../constants/SurveyChartTypes';
import SURVEY_VALUE_PRESENTATION_TYPE from '../constants/SurveyValuePresentationTypes';
import AutoCalculatedSurveyMap from './AutoCalculatedSurveyMap';

const NUMBER_OF_TICKS_ON_YAXIS = 10;

const PRESENTATION_FORMAT_MAP = {
  [SURVEY_VALUE_PRESENTATION_TYPE.Percentage]: ({ value }) => `${value}%`,
  [SURVEY_VALUE_PRESENTATION_TYPE.SignedInteger]: ({ value }) => (value > 0 ? `+${value}` : value),
  [SURVEY_VALUE_PRESENTATION_TYPE.UnsignedInteger]: ({ value }) => value
};

const SurveyChartUtils = {
  generateResponseTicks: results => {
    const isSigned = results.some(
      r => r.Presentation === SURVEY_VALUE_PRESENTATION_TYPE.SignedInteger
    );

    const values = results
      .filter(r => r.ChartType === SURVEY_CHART_TYPE.LINE)
      .flatMap(record =>
        record.SurveyResultValues.map(({ Year, Value }) => {
          if (record.IsAutoCalculated) {
            return AutoCalculatedSurveyMap[record.QuestionId].getValue({
              year: Year,
              allResults: results
            });
          }
          return Value;
        })
      );

    const min = isSigned ? Math.min(...values, 0) : 0;
    const max = Math.max(...values, 0);
    const step = (max - min) / NUMBER_OF_TICKS_ON_YAXIS;

    return Array.from(
      { length: NUMBER_OF_TICKS_ON_YAXIS + 1 },
      (_, i) => Math.round((min + step * i) * NUMBER_OF_TICKS_ON_YAXIS) / NUMBER_OF_TICKS_ON_YAXIS
    );
  },

  generateAnswerTicks: results => {
    const maxValue = chain(results)
      .filter(result => result.ChartType === SURVEY_CHART_TYPE.BAR)
      .flatMap(result => result.SurveyResultValues)
      .groupBy(entry => entry.Year)
      .map(group => sumBy(group, item => item.Value))
      .max()
      .value();

    const adjustedMax = Math.max(100, maxValue);
    const roundedMax = Math.ceil(adjustedMax / 10) * 10;

    return Array.from(
      { length: NUMBER_OF_TICKS_ON_YAXIS + 1 },
      (_, i) => (roundedMax / NUMBER_OF_TICKS_ON_YAXIS) * i
    );
  },

  formatSurveyValue: ({ value, presentation }) => {
    const formatter = PRESENTATION_FORMAT_MAP[presentation];
    return formatter({ value });
  },

  getSurveyYears: surveyResults =>
    chain(surveyResults)
      .flatMap(result => result.SurveyResultValues)
      .map('Year')
      .uniq()
      .sortBy()
      .value()
};

export default SurveyChartUtils;
